import React from 'react';

type BrandLogoProps = {
  variant?: 'mark' | 'wordmark' | 'full';
  className?: string;
  label?: string;
  src?: string; // allow drop-in asset replacement without code changes
};

/**
 * BrandLogo renders an inline Menashi shield+M mark with optional wordmark.
 * If `src` is provided, it will render an <img> using that asset instead.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({ variant = 'full', className = '', label = 'Menashi', src }) => {
  if (src) {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const fullSrc = src.startsWith('/') ? `${base}${src}` : src;
    return (
      <img
        src={fullSrc}
        alt={label}
        className={`h-8 ${className}`}
        loading="lazy"
        decoding="async"
      />
    );
  }

  const Mark = (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={`h-full w-full drop-shadow-sm ${className}`}
    >
      <defs>
        <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--brand))" />
        </linearGradient>
      </defs>
      <path
        d="M32 4l20 7v16c0 13.255-8.954 22.88-20 25-11.046-2.12-20-11.745-20-25V11l20-7z"
        fill="none"
        stroke="url(#g)"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M20 40V20l12 12 12-12v20"
        fill="none"
        stroke="url(#g)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (variant === 'mark') return <div className="h-8 w-8">{Mark}</div>;

  const Word = (
    <span className="ml-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-brand-foreground font-extrabold tracking-tight">
      MENASHI
    </span>
  );

  if (variant === 'wordmark') return (
    <div className="flex items-center">
      {Word}
    </div>
  );

  return (
    <div className="flex items-center">
      <div className="h-8 w-8">{Mark}</div>
      {Word}
    </div>
  );
};

export default BrandLogo;

